import { useEffect, useMemo, useState, type FormEvent } from 'react';

import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState } from '../../components/ui/Feedback';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { doctorDisplayName } from '../../lib/doctor-name';
import {
  useGenerateOpdSchedules,
  useOpdLookup,
  useOpdSchedules,
  useRemoveOpdSchedules,
  useUpdateOpdSchedule,
  useUpdateOpdScheduleStatus,
} from './opd.queries';
import { FieldLabel, LookupSelect } from './opd.shared';
import { formatOpdScheduleDate, formatOpdScheduleTime, todayInput } from './opd.utils';
import type { OpdSchedule } from './types';

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type ScheduleDraft = { fromTime: string; toTime: string; patientPerSlot: number };
type ConfirmAction =
  | {
      type: 'create';
      payload: {
        doctor: string;
        fromDate: string;
        toDate: string;
        days: string[];
        fromTime: string;
        toTime: string;
        timePerSlot: number;
        patientPerSlot: number;
      };
    }
  | { type: 'save'; slot: OpdSchedule; draft: ScheduleDraft }
  | { type: 'status'; ids: string[]; status: 'active' | 'inactive' }
  | { type: 'delete'; ids: string[] };

const afterDays = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

export const OpdTimeSchedulePage = () => {
  const doctors = useOpdLookup('doctors');
  const [doctor, setDoctor] = useState('');
  const [fromDate, setFromDate] = useState(todayInput);
  const [toDate, setToDate] = useState(() => afterDays(6));
  const [searched, setSearched] = useState(false);
  const schedules = useOpdSchedules(searched ? doctor : '', fromDate, toDate);
  const generate = useGenerateOpdSchedules();
  const update = useUpdateOpdSchedule();
  const status = useUpdateOpdScheduleStatus();
  const remove = useRemoveOpdSchedules();
  const [days, setDays] = useState<string[]>([]);
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState('17:00');
  const [timePerSlot, setTimePerSlot] = useState(15);
  const [patientPerSlot, setPatientPerSlot] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ScheduleDraft>>({});
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const busy = generate.isPending || update.isPending || status.isPending || remove.isPending || schedules.isFetching;

  useEffect(() => {
    setDrafts(Object.fromEntries((schedules.data ?? []).map((slot) => [
      slot.id,
      { fromTime: slot.fromTime, toTime: slot.toTime, patientPerSlot: slot.patientPerSlot },
    ])));
    setSelected([]);
  }, [schedules.data]);

  const rangeDays = useMemo(() => {
    const found = new Set<string>();
    const cursor = new Date(`${fromDate}T00:00:00+05:30`);
    const end = new Date(`${toDate}T23:59:59.999+05:30`);
    while (cursor <= end) {
      found.add(cursor.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Asia/Kolkata' }));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return found;
  }, [fromDate, toDate]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (doctor) setSearched(true);
  };

  const create = (event: FormEvent) => {
    event.preventDefault();
    setConfirmAction({
      type: 'create',
      payload: { doctor, fromDate, toDate, days: [...days], fromTime, toTime, timePerSlot, patientPerSlot },
    });
  };

  const toggle = (id: string) => setSelected((current) => (
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
  ));

  const openSaveConfirm = (slot: OpdSchedule) => {
    const draft = drafts[slot.id];
    if (draft) setConfirmAction({ type: 'save', slot, draft });
  };

  const openStatusConfirm = (nextStatus: 'active' | 'inactive') => {
    if (selected.length > 0) setConfirmAction({ type: 'status', ids: [...selected], status: nextStatus });
  };

  const openDeleteConfirm = () => {
    if (selected.length > 0) setConfirmAction({ type: 'delete', ids: [...selected] });
  };

  const closeConfirm = () => {
    if (!busy) setConfirmAction(null);
  };

  const confirmPendingAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'create') {
      generate.mutate(confirmAction.payload, { onSuccess: () => setConfirmAction(null) });
      return;
    }
    if (confirmAction.type === 'save') {
      update.mutate(
        { id: confirmAction.slot.id, payload: confirmAction.draft },
        { onSuccess: () => setConfirmAction(null) },
      );
      return;
    }
    if (confirmAction.type === 'status') {
      status.mutate(
        { ids: confirmAction.ids, status: confirmAction.status },
        { onSuccess: () => setConfirmAction(null) },
      );
      return;
    }
    remove.mutate(confirmAction.ids, { onSuccess: () => setConfirmAction(null) });
  };

  const updateDraft = (slot: OpdSchedule, draft: ScheduleDraft) => {
    setDrafts((current) => ({ ...current, [slot.id]: draft }));
  };

  const error = generate.error ?? update.error ?? status.error ?? remove.error;
  const unbookedSlots = schedules.data?.filter((slot) => slot.booked === 0) ?? [];
  const confirmCopy = confirmAction?.type === 'create'
    ? {
        title: 'Create schedule slots?',
        description: `${confirmAction.payload.days.length} selected day${confirmAction.payload.days.length === 1 ? '' : 's'} will be generated from ${confirmAction.payload.fromDate} to ${confirmAction.payload.toDate}.`,
        confirmLabel: 'Save',
        pendingLabel: 'Saving...',
        tone: 'default' as const,
      }
    : confirmAction?.type === 'save'
    ? {
        title: 'Save schedule slot?',
        description: `Update ${doctorDisplayName(confirmAction.slot.doctor.name)} on ${formatOpdScheduleDate(confirmAction.slot.date)} from ${formatOpdScheduleTime(confirmAction.slot.date, confirmAction.draft.fromTime)} to ${formatOpdScheduleTime(confirmAction.slot.date, confirmAction.draft.toTime)}.`,
        confirmLabel: 'Save',
        pendingLabel: 'Saving...',
        tone: 'default' as const,
      }
    : confirmAction?.type === 'status'
      ? {
          title: `${confirmAction.status === 'active' ? 'Activate' : 'Deactivate'} selected slots?`,
          description: `${confirmAction.ids.length} selected unbooked schedule slot${confirmAction.ids.length === 1 ? '' : 's'} will be marked ${confirmAction.status}.`,
          confirmLabel: confirmAction.status === 'active' ? 'Active' : 'Deactive',
          pendingLabel: 'Updating...',
          tone: 'default' as const,
        }
      : confirmAction?.type === 'delete'
        ? {
            title: 'Delete selected slots?',
            description: `${confirmAction.ids.length} selected unbooked schedule slot${confirmAction.ids.length === 1 ? '' : 's'} will be removed from the Time Schedule List.`,
            confirmLabel: 'Delete',
            pendingLabel: 'Deleting...',
            tone: 'danger' as const,
          }
        : null;

  return (
    <div className="page-stack opd-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">OPD</p>
          <h1>Time Schedule List</h1>
          <p>Create and maintain doctor appointment slots by date range.</p>
        </div>
      </div>

      <section className="content-card opd-card">
        <form className="opd-schedule-search" onSubmit={search}>
          <label>
            <FieldLabel required>Doctor</FieldLabel>
            <LookupSelect
              id="schedule-doctor"
              value={doctor}
              options={doctors.data}
              placeholder="Select Doctor"
              clearable={false}
              onChange={(value) => {
                setDoctor(value);
                setSearched(false);
              }}
            />
          </label>
          <label>
            <FieldLabel required>Select From Date</FieldLabel>
            <input
              type="date"
              min={todayInput()}
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value);
                setSearched(false);
              }}
              required
            />
          </label>
          <label>
            <FieldLabel required>Select To Date</FieldLabel>
            <input
              type="date"
              min={fromDate}
              value={toDate}
              onChange={(event) => {
                setToDate(event.target.value);
                setSearched(false);
              }}
              required
            />
          </label>
          <button className="button button-primary" disabled={!doctor || busy}>Search Schedule</button>
          <button
            className="button button-secondary"
            type="button"
            onClick={() => {
              setDoctor('');
              setSearched(false);
            }}
          >
            Reset
          </button>
        </form>

        {searched ? (
          <>
            <form className="opd-schedule-create" onSubmit={create}>
              <label className="opd-days">
                <FieldLabel required>Select Days</FieldLabel>
                <span>
                  {weekdays.map((day) => (
                    <label key={day} className={!rangeDays.has(day) ? 'disabled' : ''}>
                      <input
                        type="checkbox"
                        disabled={!rangeDays.has(day)}
                        checked={days.includes(day)}
                        onChange={() => setDays((current) => (
                          current.includes(day) ? current.filter((item) => item !== day) : [...current, day]
                        ))}
                      />
                      {day.slice(0, 3)}
                    </label>
                  ))}
                </span>
              </label>
              <label>
                <FieldLabel required>From Time</FieldLabel>
                <input type="time" value={fromTime} onChange={(event) => setFromTime(event.target.value)} required />
              </label>
              <label>
                <FieldLabel required>To Time</FieldLabel>
                <input type="time" value={toTime} onChange={(event) => setToTime(event.target.value)} required />
              </label>
              <label>
                <FieldLabel required>Time Per Slot (In min)</FieldLabel>
                <input
                  type="number"
                  min={1}
                  value={timePerSlot}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/^0+(?=\d)/, '');
                    event.target.value = raw;
                    setTimePerSlot(raw === '' ? 0 : Number(raw));
                  }}
                  required
                />
              </label>
              <label>
                <FieldLabel required>Patient Per Slot</FieldLabel>
                <input
                  type="number"
                  min={1}
                  value={patientPerSlot}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/^0+(?=\d)/, '');
                    event.target.value = raw;
                    setPatientPerSlot(raw === '' ? 0 : Number(raw));
                  }}
                  required
                />
              </label>
              <button className="button button-primary" disabled={days.length === 0 || busy}>+ Save</button>
            </form>

            <div className="opd-schedule-bulk">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(schedules.data?.length) && selected.length === unbookedSlots.length}
                  onChange={(event) => setSelected(event.target.checked ? unbookedSlots.map((slot) => slot.id) : [])}
                />
                Select unbooked
              </label>
              <button type="button" disabled={selected.length === 0 || busy} onClick={() => openStatusConfirm('active')}>
                Active
              </button>
              <button type="button" disabled={selected.length === 0 || busy} onClick={() => openStatusConfirm('inactive')}>
                Deactive
              </button>
              <button className="danger" type="button" disabled={selected.length === 0 || busy} onClick={openDeleteConfirm}>
                Delete
              </button>
            </div>

            {error ? (
              <div className="inline-alert">
                {error instanceof ApiError ? error.message : 'Schedule could not be updated.'}
              </div>
            ) : null}
            {schedules.isLoading ? (
              <TableSkeleton rows={7} columns={9} label="Loading schedule slots" />
            ) : schedules.data?.length === 0 ? (
              <EmptyState title="No schedule slots" message="Choose days and timing above to generate slots." />
            ) : (
              <div className="table-scroll">
                <table className="opd-table opd-schedule-table">
                  <thead>
                    <tr>
                      <th>Is active?</th>
                      <th>Doctor Name</th>
                      <th>Date (DD-MM-YYYY)</th>
                      <th>Timing</th>
                      <th>From (Slot)</th>
                      <th>To (Slot)</th>
                      <th>Patient Per Slot</th>
                      <th>Booked</th>
                      <th>Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.data?.map((slot) => {
                      const draft = drafts[slot.id] ?? {
                        fromTime: slot.fromTime,
                        toTime: slot.toTime,
                        patientPerSlot: slot.patientPerSlot,
                      };
                      return (
                        <tr key={slot.id}>
                          <td>
                            <input
                              type="checkbox"
                              disabled={slot.booked > 0}
                              checked={selected.includes(slot.id)}
                              onChange={() => toggle(slot.id)}
                            />
                            <span className={`opd-badge ${slot.status}`}>{slot.status}</span>
                          </td>
                          <td>{doctorDisplayName(slot.doctor.name)}</td>
                          <td>{formatOpdScheduleDate(slot.date)}</td>
                          <td>{formatOpdScheduleTime(slot.date, slot.fromTime)} - {formatOpdScheduleTime(slot.date, slot.toTime)}</td>
                          <td>
                            <input
                              type="time"
                              value={draft.fromTime}
                              onChange={(event) => updateDraft(slot, { ...draft, fromTime: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={draft.toTime}
                              onChange={(event) => updateDraft(slot, { ...draft, toTime: event.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={Math.max(1, slot.booked)}
                              value={draft.patientPerSlot}
                              onChange={(event) => {
                                const raw = event.target.value.replace(/^0+(?=\d)/, '');
                                event.target.value = raw;
                                updateDraft(slot, { ...draft, patientPerSlot: raw === '' ? 0 : Number(raw) });
                              }}
                            />
                          </td>
                          <td>{slot.booked}</td>
                          <td>
                            <button type="button" disabled={busy} onClick={() => openSaveConfirm(slot)}>Save</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirmCopy !== null}
        title={confirmCopy?.title ?? ''}
        description={confirmCopy?.description ?? ''}
        confirmLabel={confirmCopy?.confirmLabel ?? 'Confirm'}
        {...(confirmCopy ? { pendingLabel: confirmCopy.pendingLabel, tone: confirmCopy.tone } : {})}
        pending={busy}
        onCancel={closeConfirm}
        onConfirm={confirmPendingAction}
      />
    </div>
  );
};
